import Vue from 'vue';
import Vuex, {Commit} from 'vuex';
import {httpClient} from '@/main';
import {AxiosPromise} from 'axios';
import {changeCompany, changeLanguage, fetchLocalization, fetchStatus, refresh} from '@/api';
import VueI18n from 'vue-i18n';
import {getRefreshToken, setRefreshToken} from '@/utils';
import {Status} from '@/models/AppModel';
import LoginModule from '@/store/modules/loginModule';
import PlanModule from './modules/planModule';
import PlanEditModule from '@/store/modules/planEditModule';
import PlanEditResultModule from '@/store/modules/planEditResultModule';
import PlanEditResultEditModule from '@/store/modules/PlanEditResultEditModule';

Vue.use(Vuex);

export type RootState = {
    loading: boolean;
    token: string | null;
    authorized: boolean;
    status: Status | null;
    planResultIsEditing: boolean;
};

export const store = new Vuex.Store<RootState>({
    state: {
        loading: false,
        token: null,
        authorized: false,
        status: null,
        planResultIsEditing: false,
    },
    getters: {
        isAdmin: (state, getters) => getters.user && 'allowAdminApp' in getters.user,
        user: state => (state.token ? JSON.parse(atob(state.token.split('.')[1])) : null),
        companies: state => state.status?.CompanyDescriptions,
        equipmentTypes: state => state.status?.EquipmentTypeDescriptions,
        idCurrentCompany: state => state.status?.IdCurrentCompany,
        hideableProperties: state => state.status?.HideableProperties,
    },
    mutations: {
        setAuthorized: (state, status) => (state.authorized = status),
        setStatus: (state, status) => (state.status = status),
        setPlanResultIsEditing: (state, bool) => (state.planResultIsEditing = bool),
        setToken: (state, token) => {
            httpClient.defaults.headers.Authorization = `Bearer ${token}`;
            state.token = token;
        },
        setLoading: (state, bool) => (state.loading = bool),
    },
    actions: {
        getLocalization: ({commit}, i18n: VueI18n) => {
            return new Promise(async resolve => {
                const {data} = await fetchLocalization();
                i18n.locale = data.Culture;
                i18n.setLocaleMessage(data.Culture, data.Dictionary);
                resolve();
            });
        },
        getStatus: ({commit}) => {
            return new Promise(async (resolve, reject) => {
                try {
                    const {data} = await fetchStatus();
                    commit('setStatus', data);
                    commit('setAuthorized', true);
                    resolve();
                } catch (e) {
                    reject(e);
                }
            });
        },
        changeLanguage: async ({commit}, {i18n, culture}: {i18n: VueI18n; culture: string}) => {
            if (i18n.locale === culture) return;
            try {
                const {data} = await changeLanguage(culture);
                i18n.locale = data.Culture;
                i18n.setLocaleMessage(data.Culture, data.Dictionary);
            } catch (e) {
                console.log(e);
            }
        },
        changeCompany: async ({commit}, idCompany: number) => {
            try {
                await changeCompany(idCompany);
                location.reload();
            } catch (e) {
                console.log(e);
            }
        },
        init: async ({commit, dispatch}, i18n: VueI18n) => {
            commit('setLoading', true);
            try {
                await dispatch('getLocalization', i18n);
                setupHttpInterceptor(commit);
                try {
                    await dispatch('getStatus');
                } catch (e) {
                    if (e?.response.status === 401) {
                        commit('setAuthorized', false);
                        setRefreshToken(null);
                    }
                    throw e;
                }
            } finally {
                commit('setLoading', false);
            }
        },
    },
    modules: {
        login: LoginModule,
        plan: PlanModule,
        planEdit: PlanEditModule,
        planEditResult: PlanEditResultModule,
        planEditResultEdit: PlanEditResultEditModule,
    },
});

function setupHttpInterceptor(commit: Commit) {
    let refreshRequest: null | AxiosPromise = null,
        clearRefreshRequestId: any = null;
    httpClient.interceptors.response.use(undefined, async error => {
        if (error?.response.status === 401 && error.config.url !== 'login/refresh' && !error.config.retry) {
            if (!getRefreshToken()) return Promise.reject(error);
            if (!refreshRequest) refreshRequest = refresh();
            const {data} = await refreshRequest;
            commit('setToken', data);

            const newRequest = {
                ...error.config,
                retry: true,
            };

            newRequest.headers.Authorization = `Bearer ${data}`;

            if (!clearRefreshRequestId)
                clearRefreshRequestId = setTimeout(() => {
                    refreshRequest = null;
                    clearRefreshRequestId = null;
                }, 10e3);
            return httpClient.request(newRequest);
        } else if (error?.response.status === 401) {
            commit('setAuthorized', false);
        }

        let showError = true;
        if (error.response.status === 400 && error.config?.is400ErrorHandled) showError = false;
        if (showError)
            Vue.toasted.error(error, {
                icon: 'mdi-message-alert',
            });

        return Promise.reject(error);
    });
}
